import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  getAllBrandsData,
  updatePaymentMethod,
  loadPolicyDetails,
} from '../../../actions';

import {
  useWebViewReskin,
  useResponsive,
  useStripeT177101,
} from '../../../hooks';

import BillingDetailsCard from './BillingDetailsCard';
import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';
import PaymentMethodCard from './PaymentMethodCard';
import {
  EditPaymentMultiPolicyModal,
} from '../../settings/EditPaymentMultiPolicyModal';

import style from './css/BillingInfoScreen.module.css';

const BillingInfoScreen = ({ policyNumber }) => {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();
  const useMobileReskin = useWebViewReskin();
  const { isMobile } = useResponsive();
  const store = useSelector(({ billingPayments, policies }) => ({
    billingPayments,
    policies,
  }), shallowEqual);
  const useStripe = useStripeT177101();

  const {
    billingPayments: {
      loadingBillingInfo,
      updatingPaymentMethod,
      policyDetails,
    },
  } = store;
  const { allBrandsData, loadingAllBrandsData } = store.billingPayments;
  const isLoading = loadingBillingInfo
    || updatingPaymentMethod || loadingAllBrandsData;

  const getPolicyInformation = React.useMemo(() => {
    if (loadingAllBrandsData) {
      return {};
    }

    return allBrandsData.find((policy) => policy.PolicyNumber === policyNumber)
      || allBrandsData[0];
  }, [allBrandsData, loadingAllBrandsData]);

  useEffect(() => {
    if (policyNumber) {
      dispatch(getAllBrandsData());
      dispatch(loadPolicyDetails(policyNumber));
    }
  }, [policyNumber]);

  const onUpdatePaymentMethod = ({
    paymentMethodData,
    policies,
    sameBillingAddress,
    userInformation,
  }) => {
    const oneIncData = useStripe
      ? {}
      : {
        ...paymentMethodData,
        policyNumber,
      };
    const stripePaymentData = useStripe ? paymentMethodData : {};

    dispatch(updatePaymentMethod({
      paymentMethodData: oneIncData,
      policies,
      sameBillingAddress,
      stripePaymentData,
      userInformation,
    }));
  };

  return (
    <div className={style.billingInfoScreenContainer}>
      {!useMobileReskin && isMobile ? (
        <h1 className={style.billingInfoScreenTitle}>
          {t('billingDetailsCard.title')}
        </h1>
      ) : null}

      <EditPaymentMultiPolicyModal
        onUpdatePaymentMethod={onUpdatePaymentMethod}
      />

      <FigoLoadingOverlay visible={isLoading} />

      <BillingDetailsCard policyInformation={getPolicyInformation} />

      <h1
        className={useMobileReskin ? style.billingInfoScreenTitleResponsive
          : style.billingInfoScreenTitle}
      >
        {t('billingDetailsCard.paymentMethod')}
      </h1>

      <PaymentMethodCard
        diamondPolicyId={policyDetails?.PolicyId}
        onUpdatePaymentMethod={onUpdatePaymentMethod}
        policyInformation={getPolicyInformation}
        policyNumber={policyNumber}
      />
    </div>
  );
};

export default BillingInfoScreen;
